import type { Rng } from '@/engine/rng';
import type { RoomDef, RoomKind, DecorKind } from './types';

export interface FurnishItem {
  kind: string;
  at: 'N' | 'S' | 'E' | 'W' | 'C' | 'NW' | 'NE' | 'SW' | 'SE' | 'ANY';
  t?: number;
  dx?: number;
  dy?: number;
  id?: string;
  flip?: boolean;
  data?: Record<string, unknown>;
}
type Template = (room: RoomDef, rng: Rng) => FurnishItem[];

const big = (r: RoomDef) => r.w * r.h >= 120;

export const TEMPLATES: Partial<Record<RoomKind, Template>> = {
  bedroom: (r, rng) => [
    { kind: 'bed', at: 'NW' },
    { kind: 'nightstand', at: 'N', t: 0.35 },
    { kind: 'wardrobe', at: 'N', t: 0.8 },
    { kind: 'dresser', at: 'E', t: 0.3 },
    { kind: 'rugRound', at: 'C' },
    ...(rng.chance(0.6) ? [{ kind: 'armchair', at: 'SE' as const }] : []),
    ...(rng.chance(0.5) ? [{ kind: 'plant', at: 'SW' as const }] : []),
    ...(rng.chance(0.5) ? [{ kind: 'lamp', at: 'NE' as const }] : []),
  ],
  guest: (r, rng) => [
    { kind: 'bedSmall', at: 'NW' },
    { kind: rng.chance(0.5) ? 'wardrobe' : 'dresser', at: 'N', t: 0.7 },
    { kind: 'chair', at: 'E', t: 0.6 },
    { kind: 'nightstand', at: 'N', t: 0.3 },
    ...(rng.chance(0.5) ? [{ kind: 'rugBlue', at: 'C' as const }] : []),
    ...(rng.chance(0.4) ? [{ kind: 'plant', at: 'SE' as const }] : []),
  ],
  master: () => [
    { kind: 'bed', at: 'N', t: 0.5 },
    { kind: 'nightstand', at: 'N', t: 0.28 },
    { kind: 'nightstand', at: 'N', t: 0.72 },
    { kind: 'wardrobe', at: 'NE' },
    { kind: 'wardrobe', at: 'NW' },
    { kind: 'dresser', at: 'W', t: 0.6 },
    { kind: 'mirrorStand', at: 'E', t: 0.5 },
    { kind: 'rug', at: 'C', dy: 2 },
    { kind: 'sofaRed', at: 'S', t: 0.5 },
    { kind: 'lamp', at: 'SE' },
    { kind: 'plant', at: 'SW' },
  ],
  study: () => [
    { kind: 'desk', at: 'N', t: 0.5 },
    { kind: 'chair', at: 'C', dy: -1 },
    { kind: 'bookshelf', at: 'NW' },
    { kind: 'bookshelf', at: 'NE' },
    { kind: 'cabinet', at: 'W', t: 0.6 },
    { kind: 'armchair', at: 'SE' },
    { kind: 'globe', at: 'E', t: 0.3 },
    { kind: 'rugRound', at: 'C', dy: 2 },
    { kind: 'lamp', at: 'SW' },
  ],
  library: (r) => [
    { kind: 'bookshelf', at: 'N', t: 0.08 },
    { kind: 'bookshelf', at: 'N', t: 0.26 },
    { kind: 'bookshelf', at: 'N', t: 0.62 },
    { kind: 'bookshelf', at: 'N', t: 0.8 },
    { kind: 'bookshelf', at: 'W', t: 0.3 },
    { kind: 'bookshelf', at: 'W', t: 0.6 },
    { kind: 'bookshelf', at: 'E', t: 0.3 },
    { kind: 'bookshelf', at: 'E', t: 0.7 },
    { kind: 'fireplace', at: 'N', t: 0.45 },
    { kind: 'table', at: 'C', dx: -3 },
    { kind: 'chair', at: 'C', dx: -5 },
    { kind: 'chair', at: 'C', dx: 0 },
    { kind: 'sofa', at: 'C', dx: 4, dy: 3 },
    { kind: 'armchair', at: 'C', dx: 5, dy: -3 },
    { kind: 'rug', at: 'C', dx: 4, dy: 5 },
    { kind: 'globe', at: 'SE' },
    { kind: 'bookPile', at: 'ANY' },
    { kind: 'bookPile', at: 'ANY' },
    { kind: 'lamp', at: 'SW' },
    ...(big(r) ? [{ kind: 'statue', at: 'S' as const, t: 0.5 }, { kind: 'plant', at: 'S' as const, t: 0.2 }] : []),
  ],
  secret: () => [
    { kind: 'desk', at: 'N', t: 0.5 },
    { kind: 'chair', at: 'C', dy: -1 },
    { kind: 'bookshelf', at: 'NW' },
    { kind: 'candelabra', at: 'NE' },
    { kind: 'chest', at: 'S', t: 0.7 },
    { kind: 'bookPile', at: 'ANY' },
  ],
  dining: (r) => [
    { kind: 'tableLong', at: 'C', dx: r.w > 16 ? -4 : 0 },
    ...(r.w > 16 ? [{ kind: 'tableLong', at: 'C' as const, dx: 1 }] : []),
    { kind: 'chair', at: 'C', dx: -5, dy: -2 },
    { kind: 'chair', at: 'C', dx: -2, dy: -2 },
    { kind: 'chair', at: 'C', dx: 1, dy: -2 },
    { kind: 'chair', at: 'C', dx: 4, dy: -2 },
    { kind: 'chair', at: 'C', dx: -5, dy: 3 },
    { kind: 'chair', at: 'C', dx: -2, dy: 3 },
    { kind: 'chair', at: 'C', dx: 1, dy: 3 },
    { kind: 'chair', at: 'C', dx: 4, dy: 3 },
    { kind: 'cabinet', at: 'N', t: 0.15 },
    { kind: 'cabinet', at: 'N', t: 0.85 },
    { kind: 'fireplace', at: 'N', t: 0.5 },
    { kind: 'plant', at: 'SW' },
    { kind: 'plant', at: 'SE' },
    { kind: 'candelabra', at: 'W', t: 0.5 },
  ],
  kitchen: () => [
    { kind: 'stove', at: 'N', t: 0.2 },
    { kind: 'counter', at: 'N', t: 0.45 },
    { kind: 'sink', at: 'N', t: 0.62 },
    { kind: 'counter', at: 'N', t: 0.8 },
    { kind: 'shelf', at: 'W', t: 0.4 },
    { kind: 'table', at: 'C' },
    { kind: 'chair', at: 'C', dx: -2 },
    { kind: 'barrel', at: 'SW' },
    { kind: 'barrel', at: 'S', t: 0.2 },
    { kind: 'crate', at: 'SE' },
    { kind: 'sack', at: 'S', t: 0.7 },
    { kind: 'bucket', at: 'ANY' },
  ],
  pantry: () => [
    { kind: 'shelf', at: 'N', t: 0.2 },
    { kind: 'shelf', at: 'N', t: 0.7 },
    { kind: 'barrel', at: 'W', t: 0.6 },
    { kind: 'sack', at: 'E', t: 0.5 },
    { kind: 'crate', at: 'SE' },
    { kind: 'sack', at: 'S', t: 0.3 },
  ],
  bath: () => [
    { kind: 'bathtub', at: 'N', t: 0.6 },
    { kind: 'sink', at: 'N', t: 0.1 },
    { kind: 'toilet', at: 'W', t: 0.8 },
    { kind: 'tub', at: 'S', t: 0.7 },
    { kind: 'puddle', at: 'ANY' },
  ],
  washroom: () => [
    { kind: 'sink', at: 'N', t: 0.3 },
    { kind: 'toilet', at: 'N', t: 0.7 },
    { kind: 'bucket', at: 'ANY' },
  ],
  ballroom: (r) => [
    { kind: 'pillar', at: 'C', dx: -r.w * 0.3, dy: -r.h * 0.3 },
    { kind: 'pillar', at: 'C', dx: r.w * 0.3, dy: -r.h * 0.3 },
    { kind: 'pillar', at: 'C', dx: -r.w * 0.3, dy: r.h * 0.3 },
    { kind: 'pillar', at: 'C', dx: r.w * 0.3, dy: r.h * 0.3 },
    { kind: 'piano', at: 'N', t: 0.5 },
    { kind: 'sofaRed', at: 'W', t: 0.3 },
    { kind: 'sofaRed', at: 'W', t: 0.7 },
    { kind: 'sofaRed', at: 'E', t: 0.3 },
    { kind: 'sofaRed', at: 'E', t: 0.7 },
    { kind: 'tableRound', at: 'SW' },
    { kind: 'tableRound', at: 'SE' },
    { kind: 'chair', at: 'S', t: 0.15 },
    { kind: 'chair', at: 'S', t: 0.85 },
    { kind: 'candelabra', at: 'N', t: 0.2 },
    { kind: 'candelabra', at: 'N', t: 0.8 },
    { kind: 'statue', at: 'N', t: 0.05 },
    { kind: 'statue', at: 'N', t: 0.95 },
    { kind: 'plant', at: 'S', t: 0.4 },
    { kind: 'plant', at: 'S', t: 0.6 },
  ],
  conservatory: () => [
    { kind: 'plant', at: 'N', t: 0.1 }, { kind: 'plant', at: 'N', t: 0.3 }, { kind: 'plant', at: 'N', t: 0.5 }, { kind: 'plant', at: 'N', t: 0.7 }, { kind: 'plant', at: 'N', t: 0.9 },
    { kind: 'plant', at: 'W', t: 0.4 }, { kind: 'plant', at: 'W', t: 0.8 }, { kind: 'plant', at: 'E', t: 0.4 }, { kind: 'plant', at: 'E', t: 0.8 },
    { kind: 'tableRound', at: 'C' },
    { kind: 'chair', at: 'C', dx: -2 }, { kind: 'chair', at: 'C', dx: 3 },
    { kind: 'bench', at: 'S', t: 0.3 },
    { kind: 'statue', at: 'S', t: 0.7 },
    { kind: 'plant', at: 'ANY' }, { kind: 'plant', at: 'ANY' }, { kind: 'bucket', at: 'ANY' }, { kind: 'puddle', at: 'ANY' },
  ],
  music: () => [
    { kind: 'piano', at: 'N', t: 0.5 },
    { kind: 'sofa', at: 'S', t: 0.5 },
    { kind: 'armchair', at: 'W', t: 0.5 },
    { kind: 'armchair', at: 'E', t: 0.5 },
    { kind: 'cabinet', at: 'NW' },
    { kind: 'candelabra', at: 'NE' },
    { kind: 'rugRound', at: 'C', dy: 1 },
    { kind: 'chair', at: 'C', dy: -1 },
  ],
  billiard: () => [
    { kind: 'billiard', at: 'C' },
    { kind: 'cabinet', at: 'N', t: 0.2 },
    { kind: 'sofa', at: 'N', t: 0.7 },
    { kind: 'armchair', at: 'SW' },
    { kind: 'armchair', at: 'SE' },
    { kind: 'chest', at: 'S', t: 0.5 },
    { kind: 'plant', at: 'NE' },
  ],
  drawing: () => [
    { kind: 'fireplace', at: 'N', t: 0.5 },
    { kind: 'sofa', at: 'C', dy: -1 },
    { kind: 'tableRound', at: 'C', dy: 2 },
    { kind: 'armchair', at: 'C', dx: -4, dy: 2 },
    { kind: 'armchair', at: 'C', dx: 4, dy: 2 },
    { kind: 'cabinet', at: 'NW' },
    { kind: 'clock', at: 'NE' },
    { kind: 'rug', at: 'C', dy: 3 },
    { kind: 'plant', at: 'SW' },
    { kind: 'lamp', at: 'SE' },
  ],
  smoking: () => [
    { kind: 'armchair', at: 'N', t: 0.3 }, { kind: 'armchair', at: 'N', t: 0.7 },
    { kind: 'tableRound', at: 'C' },
    { kind: 'cabinet', at: 'W', t: 0.5 },
    { kind: 'sofa', at: 'S', t: 0.5 },
    { kind: 'plant', at: 'NE' },
  ],
  cloak: () => [
    { kind: 'wardrobe', at: 'N', t: 0.2 }, { kind: 'wardrobe', at: 'N', t: 0.5 }, { kind: 'wardrobe', at: 'N', t: 0.8 },
    { kind: 'bench', at: 'S', t: 0.5 },
    { kind: 'mirrorStand', at: 'E', t: 0.6 },
  ],
  cellar: () => [
    { kind: 'wineRack', at: 'N', t: 0.15 }, { kind: 'wineRack', at: 'N', t: 0.4 }, { kind: 'wineRack', at: 'N', t: 0.65 }, { kind: 'wineRack', at: 'N', t: 0.9 },
    { kind: 'barrel', at: 'W', t: 0.3 }, { kind: 'barrel', at: 'W', t: 0.5 }, { kind: 'barrel', at: 'W', t: 0.7 },
    { kind: 'barrel', at: 'E', t: 0.4 }, { kind: 'barrel', at: 'E', t: 0.6 },
    { kind: 'crateStack', at: 'SE' },
    { kind: 'table', at: 'C' },
    { kind: 'crate', at: 'ANY' }, { kind: 'puddle', at: 'ANY' },
  ],
  boiler: () => [
    { kind: 'boiler', at: 'N', t: 0.5 },
    { kind: 'coalPile', at: 'NW' },
    { kind: 'crate', at: 'SE' },
    { kind: 'barrel', at: 'E', t: 0.4 },
    { kind: 'bucket', at: 'ANY' },
    { kind: 'puddle', at: 'ANY' },
  ],
  laundry: () => [
    { kind: 'tub', at: 'N', t: 0.3 }, { kind: 'tub', at: 'N', t: 0.7 },
    { kind: 'table', at: 'C' },
    { kind: 'shelf', at: 'W', t: 0.5 },
    { kind: 'sack', at: 'SE' }, { kind: 'bucket', at: 'ANY' }, { kind: 'puddle', at: 'ANY' },
  ],
  storage: (r) => [
    { kind: 'crateStack', at: 'NW' },
    { kind: 'shelf', at: 'N', t: 0.6 },
    { kind: 'crate', at: 'E', t: 0.3 }, { kind: 'crate', at: 'E', t: 0.5 },
    { kind: 'barrel', at: 'SW' },
    { kind: 'sack', at: 'S', t: 0.5 },
    { kind: 'chest', at: 'S', t: 0.8 },
    ...(big(r) ? [{ kind: 'crateStack', at: 'SE' as const }, { kind: 'mannequin', at: 'ANY' as const }] : []),
  ],
  cell: () => [
    { kind: 'cot', at: 'NW' },
    { kind: 'bucket', at: 'SE' },
    { kind: 'bones', at: 'ANY' },
  ],
  well: () => [
    { kind: 'well', at: 'C' },
    { kind: 'bucket', at: 'ANY' },
    { kind: 'puddle', at: 'ANY' }, { kind: 'puddle', at: 'ANY' },
  ],
  tunnel: () => [{ kind: 'rubble', at: 'ANY' }, { kind: 'puddle', at: 'ANY' }, { kind: 'bones', at: 'ANY' }],
  coal: () => [
    { kind: 'coalPile', at: 'N', t: 0.3 }, { kind: 'coalPile', at: 'N', t: 0.7 }, { kind: 'coalPile', at: 'W', t: 0.7 },
    { kind: 'sack', at: 'SE' }, { kind: 'bucket', at: 'ANY' },
  ],
  ice: () => [
    { kind: 'iceBlock', at: 'N', t: 0.2 }, { kind: 'iceBlock', at: 'N', t: 0.5 }, { kind: 'iceBlock', at: 'N', t: 0.8 },
    { kind: 'iceBlock', at: 'W', t: 0.6 }, { kind: 'crate', at: 'SE' }, { kind: 'puddle', at: 'ANY' },
  ],
  gallery: () => [
    { kind: 'statue', at: 'N', t: 0.1 }, { kind: 'statue', at: 'N', t: 0.9 },
    { kind: 'bench', at: 'C', dx: -6 }, { kind: 'bench', at: 'C', dx: 6 },
    { kind: 'plant', at: 'SW' }, { kind: 'plant', at: 'SE' },
  ],
  nursery: () => [
    { kind: 'crib', at: 'NW' },
    { kind: 'bedSmall', at: 'NE' },
    { kind: 'toybox', at: 'S', t: 0.3 },
    { kind: 'rockingHorse', at: 'E', t: 0.5 },
    { kind: 'dollShelf', at: 'N', t: 0.5 },
    { kind: 'doll', at: 'ANY' },
    { kind: 'rugRound', at: 'C' },
  ],
  sewing: () => [
    { kind: 'sewingMachine', at: 'N', t: 0.3 },
    { kind: 'mannequin', at: 'N', t: 0.7 },
    { kind: 'mannequin', at: 'E', t: 0.5 },
    { kind: 'table', at: 'C' },
    { kind: 'chair', at: 'C', dx: -2 },
    { kind: 'dresser', at: 'W', t: 0.5 },
    { kind: 'sack', at: 'SE' },
  ],
  dressing: () => [
    { kind: 'wardrobe', at: 'N', t: 0.2 }, { kind: 'wardrobe', at: 'N', t: 0.5 }, { kind: 'wardrobe', at: 'N', t: 0.8 },
    { kind: 'mirrorStand', at: 'W', t: 0.5 },
    { kind: 'dresser', at: 'E', t: 0.5 },
    { kind: 'mannequin', at: 'SW' },
    { kind: 'bench', at: 'S', t: 0.5 },
  ],
  closet: () => [
    { kind: 'wardrobePush', at: 'N', t: 0.5, id: 'closet-wardrobe' },
    { kind: 'shelf', at: 'W', t: 0.5 },
    { kind: 'crate', at: 'SE' },
    { kind: 'bucket', at: 'ANY' },
  ],
  servant: () => [
    { kind: 'bedSmall', at: 'NW' },
    { kind: 'bedSmall', at: 'NE' },
    { kind: 'dresser', at: 'N', t: 0.5 },
    { kind: 'chair', at: 'S', t: 0.5 },
    { kind: 'bucket', at: 'ANY' },
  ],
  sick: () => [
    { kind: 'cot', at: 'N', t: 0.2 }, { kind: 'cot', at: 'N', t: 0.5 }, { kind: 'cot', at: 'N', t: 0.8 },
    { kind: 'cabinet', at: 'W', t: 0.6 },
    { kind: 'table', at: 'S', t: 0.5 },
    { kind: 'chair', at: 'E', t: 0.6 },
    { kind: 'bucket', at: 'ANY' },
  ],
  archive: () => [
    { kind: 'bookshelf', at: 'N', t: 0.1 }, { kind: 'bookshelf', at: 'N', t: 0.4 }, { kind: 'bookshelf', at: 'N', t: 0.7 },
    { kind: 'bookshelf', at: 'W', t: 0.5 }, { kind: 'bookshelf', at: 'E', t: 0.5 },
    { kind: 'desk', at: 'C' },
    { kind: 'chair', at: 'C', dy: 2 },
    { kind: 'bookPile', at: 'ANY' }, { kind: 'bookPile', at: 'ANY' }, { kind: 'chest', at: 'S', t: 0.5 },
  ],
  doll: () => [
    { kind: 'dollShelf', at: 'N', t: 0.2 }, { kind: 'dollShelf', at: 'N', t: 0.6 },
    { kind: 'doll', at: 'ANY' }, { kind: 'doll', at: 'ANY' }, { kind: 'doll', at: 'ANY' }, { kind: 'doll', at: 'ANY' },
    { kind: 'rockingHorse', at: 'SE' },
    { kind: 'bedSmall', at: 'W', t: 0.5 },
    { kind: 'tableRound', at: 'C' },
    { kind: 'chair', at: 'C', dx: -2 }, { kind: 'chair', at: 'C', dx: 3 },
  ],
  clock: () => [
    { kind: 'clock', at: 'N', t: 0.2 }, { kind: 'clock', at: 'N', t: 0.5 }, { kind: 'clock', at: 'N', t: 0.8 },
    { kind: 'clock', at: 'W', t: 0.5 }, { kind: 'clock', at: 'E', t: 0.5 },
    { kind: 'desk', at: 'S', t: 0.5 },
    { kind: 'chair', at: 'C' },
  ],
  observatory: () => [
    { kind: 'telescope', at: 'N', t: 0.5 },
    { kind: 'desk', at: 'W', t: 0.5 },
    { kind: 'chair', at: 'C', dx: -3 },
    { kind: 'globe', at: 'E', t: 0.4 },
    { kind: 'bookshelf', at: 'NE' },
    { kind: 'bookPile', at: 'ANY' },
  ],
  chapel: (r) => [
    { kind: 'altar', at: 'N', t: 0.5 },
    { kind: 'lectern', at: 'N', t: 0.72 },
    { kind: 'pew', at: 'C', dx: -4, dy: -3 }, { kind: 'pew', at: 'C', dx: 4, dy: -3 },
    { kind: 'pew', at: 'C', dx: -4, dy: 0 }, { kind: 'pew', at: 'C', dx: 4, dy: 0 },
    { kind: 'pew', at: 'C', dx: -4, dy: 3 }, { kind: 'pew', at: 'C', dx: 4, dy: 3 },
    { kind: 'candelabra', at: 'NW' }, { kind: 'candelabra', at: 'NE' },
    { kind: 'coffin', at: 'SW' },
    ...(big(r) ? [{ kind: 'organ', at: 'N' as const, t: 0.15 }, { kind: 'statue', at: 'SE' as const }] : []),
  ],
  schoolroom: () => [
    { kind: 'desk', at: 'N', t: 0.5 },
    { kind: 'chair', at: 'C', dx: -4, dy: -1 }, { kind: 'chair', at: 'C', dx: 0, dy: -1 }, { kind: 'chair', at: 'C', dx: 4, dy: -1 },
    { kind: 'chair', at: 'C', dx: -4, dy: 2 }, { kind: 'chair', at: 'C', dx: 0, dy: 2 }, { kind: 'chair', at: 'C', dx: 4, dy: 2 },
    { kind: 'bookshelf', at: 'NW' }, { kind: 'globe', at: 'NE' }, { kind: 'bookPile', at: 'ANY' },
  ],
  linen: () => [
    { kind: 'shelf', at: 'N', t: 0.3 }, { kind: 'shelf', at: 'N', t: 0.7 },
    { kind: 'tub', at: 'S', t: 0.5 }, { kind: 'sack', at: 'SE' },
  ],
  attic: (r) => [
    { kind: 'crateStack', at: 'NW' }, { kind: 'crateStack', at: 'NE' },
    { kind: 'chest', at: 'N', t: 0.5 },
    { kind: 'mannequin', at: 'ANY' }, { kind: 'wardrobe', at: 'W', t: 0.6 },
    { kind: 'crate', at: 'ANY' }, { kind: 'crate', at: 'ANY' }, { kind: 'sack', at: 'ANY' }, { kind: 'bookPile', at: 'ANY' },
    { kind: 'rockingHorse', at: 'ANY' },
    ...(big(r) ? [{ kind: 'mirrorStand', at: 'E' as const, t: 0.5 }, { kind: 'globe', at: 'ANY' as const }] : []),
  ],
  tank: () => [
    { kind: 'waterTank', at: 'N', t: 0.3 }, { kind: 'waterTank', at: 'N', t: 0.7 },
    { kind: 'bucket', at: 'ANY' }, { kind: 'puddle', at: 'ANY' }, { kind: 'puddle', at: 'ANY' },
  ],
  lair: () => [
    { kind: 'nest', at: 'C' },
    { kind: 'bones', at: 'ANY' }, { kind: 'bones', at: 'ANY' }, { kind: 'bones', at: 'ANY' },
    { kind: 'crateStack', at: 'SW' }, { kind: 'sack', at: 'ANY' },
  ],
  trophy: () => [
    { kind: 'cabinet', at: 'N', t: 0.3 }, { kind: 'cabinet', at: 'N', t: 0.7 },
    { kind: 'armchair', at: 'C', dx: -3 }, { kind: 'armchair', at: 'C', dx: 3 },
    { kind: 'tableRound', at: 'C', dy: 3 },
    { kind: 'statue', at: 'SW' }, { kind: 'chest', at: 'SE' },
  ],
  balcony: () => [{ kind: 'bench', at: 'C' }, { kind: 'plant', at: 'W', t: 0.5 }, { kind: 'plant', at: 'E', t: 0.5 }],
  stairhall: () => [{ kind: 'plant', at: 'ANY' }],
  hall: (r) => [
    { kind: 'statue', at: 'NW' }, { kind: 'statue', at: 'NE' },
    { kind: 'tableRound', at: 'C', dy: 2 },
    { kind: 'plant', at: 'SW' }, { kind: 'plant', at: 'SE' },
    { kind: 'clock', at: 'W', t: 0.4 },
    { kind: 'cabinet', at: 'E', t: 0.4 },
    ...(big(r) ? [{ kind: 'rug', at: 'C' as const, dy: 5 }, { kind: 'bench', at: 'W' as const, t: 0.7 }, { kind: 'bench', at: 'E' as const, t: 0.7 }] : []),
  ],
  corridor: (r, rng) => {
    const items: FurnishItem[] = [];
    const n = Math.floor((r.w + r.h) / 10);
    for (let i = 0; i < n; i++) items.push({ kind: rng.pick(['plant', 'statue', 'chair', 'bench', 'clock', 'cabinet']), at: r.w > r.h ? 'N' : 'W', t: rng.float(0.05, 0.95) });
    return items;
  },
  passage: (r, rng) => {
    const items: FurnishItem[] = [];
    const n = Math.floor((r.w + r.h) / 12);
    for (let i = 0; i < n; i++) items.push({ kind: rng.pick(['crate', 'sack', 'bucket', 'barrel']), at: 'ANY' });
    return items;
  },
};

export const DECOR_TEMPLATES: Partial<Record<RoomKind, Array<[DecorKind, number]>>> = {
  bedroom: [['painting', 0.8], ['mirror', 0.5], ['sconce', 0.3]],
  guest: [['painting', 0.6], ['sconce', 0.3]],
  master: [['paintingBig', 1], ['painting', 0.6], ['mirror', 0.8], ['sconce', 0.7], ['sconce', 0.7]],
  study: [['painting', 0.8], ['plaque', 0.5], ['sconce', 0.5], ['clockWall', 0.4]],
  library: [['paintingBig', 1], ['painting', 0.8], ['sconce', 0.9], ['sconce', 0.9], ['plaque', 0.3]],
  secret: [['poster', 0.9], ['crack', 0.5], ['sconce', 0.9]],
  dining: [['paintingBig', 1], ['painting', 0.7], ['sconce', 0.8], ['sconce', 0.8]],
  kitchen: [['shelf', 1], ['shelf', 0.8], ['hook', 0.7], ['vent', 0.4]],
  pantry: [['shelf', 1], ['shelf', 0.7], ['hook', 0.6]],
  bath: [['mirror', 1], ['sconce', 0.5], ['crack', 0.3]],
  washroom: [['mirror', 0.8], ['crack', 0.4]],
  ballroom: [['paintingBig', 1], ['paintingBig', 0.9], ['sconce', 0.9], ['sconce', 0.9], ['sconce', 0.9], ['mirror', 0.6]],
  conservatory: [['vent', 0.3]],
  music: [['painting', 0.9], ['sconce', 0.7], ['plaque', 0.4]],
  billiard: [['antlers', 0.9], ['painting', 0.6], ['sconce', 0.6]],
  drawing: [['paintingBig', 0.9], ['painting', 0.7], ['clockWall', 0.5], ['sconce', 0.8]],
  smoking: [['antlers', 0.7], ['painting', 0.6], ['sconce', 0.5]],
  cloak: [['hook', 1], ['hook', 0.9], ['hook', 0.8], ['mirror', 0.5]],
  hall: [['paintingBig', 1], ['paintingBig', 0.9], ['sconce', 0.9], ['sconce', 0.9], ['clockWall', 0.4], ['antlers', 0.4]],
  corridor: [['painting', 0.9], ['painting', 0.7], ['painting', 0.5], ['sconce', 0.8], ['sconce', 0.6], ['sconce', 0.4], ['crack', 0.3]],
  passage: [['hook', 0.6], ['crack', 0.5], ['vent', 0.4], ['sconce', 0.3]],
  cellar: [['crack', 0.7], ['sconce', 0.5], ['hook', 0.4]],
  boiler: [['vent', 0.9], ['crack', 0.6], ['poster', 0.5]],
  laundry: [['hook', 0.8], ['shelf', 0.5], ['vent', 0.4]],
  storage: [['shelf', 0.8], ['crack', 0.5], ['hook', 0.4]],
  cell: [['crack', 0.9], ['crack', 0.6]],
  well: [['crack', 0.8], ['vent', 0.3]],
  tunnel: [['crack', 0.9], ['crack', 0.7]],
  coal: [['vent', 0.7], ['crack', 0.5]],
  ice: [['hook', 0.6], ['crack', 0.4]],
  gallery: [['paintingBig', 1], ['painting', 1], ['painting', 1], ['painting', 1], ['painting', 1], ['sconce', 0.9], ['sconce', 0.9]],
  nursery: [['poster', 0.8], ['painting', 0.6], ['shelf', 0.5]],
  sewing: [['shelf', 0.8], ['poster', 0.5], ['mirror', 0.5]],
  dressing: [['mirror', 1], ['mirror', 0.7], ['hook', 0.7], ['sconce', 0.5]],
  closet: [['hook', 0.9], ['shelf', 0.7], ['crack', 0.4]],
  servant: [['cross', 0.6], ['hook', 0.7], ['poster', 0.4]],
  sick: [['cross', 0.8], ['shelf', 0.7], ['poster', 0.5], ['crack', 0.4]],
  archive: [['plaque', 0.6], ['shelf', 0.6], ['sconce', 0.5], ['poster', 0.5]],
  doll: [['shelf', 0.9], ['painting', 0.7], ['crack', 0.5]],
  clock: [['clockWall', 1], ['clockWall', 0.9], ['clockWall', 0.8], ['clockWall', 0.6]],
  observatory: [['poster', 0.8], ['plaque', 0.5], ['sconce', 0.5]],
  chapel: [['cross', 1], ['sconce', 0.9], ['sconce', 0.9], ['painting', 0.5]],
  schoolroom: [['poster', 0.9], ['poster', 0.7], ['shelf', 0.5]],
  linen: [['shelf', 0.9], ['hook', 0.6]],
  attic: [['crack', 0.8], ['vent', 0.6], ['hook', 0.5], ['crack', 0.5]],
  tank: [['vent', 0.8], ['crack', 0.7]],
  lair: [['crack', 1], ['crack', 0.9], ['crack', 0.8]],
  trophy: [['antlers', 1], ['antlers', 0.9], ['antlers', 0.8], ['painting', 0.5], ['plaque', 0.6]],
  stairhall: [['painting', 0.6], ['sconce', 0.7]],
  balcony: [],
  empty: [['crack', 0.6]],
};
